import React from "react";
import {
	ClassNameProps,
	Column,
	formatBytes,
	If,
	makeClassName,
	Row,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { settings } from "@hex-insights/forms";
import { FileExportButton, FileLink } from "@hex-insights/router";
import { Icon } from "../../../../Components/Icon";
import { MediaAttachment } from "../../../../Utilities";
import { MediaAttachmentFormValues } from "../../../FormUtilities";
import styles from "../file-link.module.css";

export type FileAttachmentsRowProps = {
	attachments: Pick<MediaAttachment, "id" | "title" | "fileURL" | "fileContentType">[];
	attachmentClassName?: string;
	attachmentStyle?: React.CSSProperties;
} & Partial<ClassNameProps & StyleProps>;

export function FileAttachmentsRow({
	attachments,
	attachmentClassName,
	attachmentStyle,
	className,
	style,
}: FileAttachmentsRowProps) {
	return (
		<Row
			justify="spaced-start"
			horizontalSpacing="0.5rem"
			withVerticalSpacing
			verticalSpacing="0.5rem"
			overflow="wrap"
			className={className}
			style={style}
		>
			{attachments.map((attachment) => (
				<FileAttachment
					key={attachment.id}
					attachment={attachment}
					className={makeClassName(styles["file-attachments-row__file-attachment"], attachmentClassName)}
					style={attachmentStyle}
				/>
			))}
		</Row>
	);
}

export type FileAttachmentProps = Omit<FileAttachmentViewProps, "title" | "fileURL" | "fileContentType"> & {
	attachment: Pick<MediaAttachment, "title" | "fileURL" | "fileContentType">;
};

export function FileAttachment({ attachment, ...props }: FileAttachmentProps) {
	return (
		<FileAttachmentView
			title={attachment.title}
			fileURL={attachment.fileURL}
			fileContentType={attachment.fileContentType}
			{...props}
		/>
	);
}

export type FileAttachmentPreviewProps = Omit<
	FileAttachmentViewProps,
	"title" | "fileURL" | "fileContentType" | "noDownload"
> & {
	formValues: Pick<MediaAttachmentFormValues.Base, "title" | "fileKey" | "fileContentType">;
};

export function FileAttachmentPreview({ formValues, ...props }: FileAttachmentPreviewProps) {
	return (
		<FileAttachmentView
			title={formValues.title}
			fileURL={settings.uploads.fileInfoToURL(formValues.fileKey, formValues.fileContentType)}
			fileContentType={formValues.fileContentType}
			noDownload
			{...props}
		/>
	);
}

export type FileAttachmentViewProps = {
	title: string;
	fileURL: string;
	fileContentType: string;
	noDownload?: boolean;
	height?: string;
	onClick?: () => void;
	downloadLabel?: "full" | "icon-only";
} & Partial<ClassNameProps & StyleProps>;

export function FileAttachmentView({
	title,
	fileURL,
	fileContentType,
	noDownload,
	height,
	onClick,
	downloadLabel = "icon-only",
	className,
	style,
}: FileAttachmentViewProps) {
	const [fileSizeBytes, setFileSizeBytes] = React.useState(0);

	React.useEffect(() => {
		(async () => {
			try {
				const response = await fetch(fileURL, { method: "HEAD" });
				if (!response.ok) {
					setFileSizeBytes(0);
					return;
				}
				const contentLength = response.headers.get("content-length");
				if (contentLength) {
					setFileSizeBytes(parseInt(contentLength, 10));
				}
			} catch {
				setFileSizeBytes(0);
			}
		})();
	}, [fileURL]);

	const content = (
		<Row justify="space-between" align="center" style={{ flexGrow: 1 }}>
			<Row justify="spaced-start" align="center" style={{ minWidth: 0, height: "100%" }}>
				<Row justify="center" align="center" className={styles["file-attachment__file-icon-container"]}>
					<Icon.FileText />
				</Row>

				<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ overflow: "hidden" }}>
					<TruncatedText className={styles["file-attachment__name"]}>{title || "File"}</TruncatedText>
					<If condition={fileSizeBytes > 0}>
						<TruncatedText className={styles["file-attachment__size"]}>{formatBytes(fileSizeBytes)}</TruncatedText>
					</If>
				</Column>
			</Row>

			<If condition={!noDownload}>
				<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
					<Icon.DownloadSimple className={styles["file-attachment__download-icon"]} />
					<If condition={downloadLabel === "full"}>
						<span className={styles["file-attachment__download-text"]}>Download</span>
					</If>
				</Row>
			</If>
		</Row>
	);

	const fullStyle = { "--attachment---height": height, ...style } as React.CSSProperties;

	if (noDownload) {
		return (
			<div onClick={onClick} className={makeClassName(styles["file-attachment"], className)} style={fullStyle}>
				{content}
			</div>
		);
	}

	if (fileContentType === "application/pdf") {
		return (
			<FileLink
				name={title}
				url={fileURL}
				className={makeClassName(styles["file-attachment"], className)}
				style={fullStyle}
			>
				{content}
			</FileLink>
		);
	}

	return (
		<FileExportButton
			name={title}
			url={fileURL}
			className={makeClassName(styles["file-attachment"], className)}
			style={fullStyle}
		>
			{content}
		</FileExportButton>
	);
}
